import { useMemo, useEffect } from "react";
import { v4 } from "uuid";

import {
  IMessageStore,
  MessageStore,
} from "@/components/Playground/Messages/Message/message-store";
import { usePlayground, PlaygroundStore } from "@/components/Playground/playground-store";
import { DEFAULT_OUTPUT_PAGE_SIZE, VariableSetOperation } from "@/components/Playground/types";
import { useAuth } from "@/context/auth-context";
import { RunGroupsCursor, useGetRunGroups } from "@/queries/playground/useSessionRuns";
import { IndividualRun } from "@/types/metadata";

const toVariableValue = (value: unknown): string | object => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "object" && value !== null) {
    return value;
  }
  return String(value ?? "");
};

const useInitialOutputs = (beforeCursor: RunGroupsCursor | null) => {
  const playground = usePlayground();
  const userToken = useAuth()?.userToken ?? "";

  const {
    data: initialRunGroupData,
    isFetching,
    isLoading,
    isPreviousData,
  } = useGetRunGroups(
    playground.workspace_id,
    playground.instanceId,
    { beforeCursor, perPage: DEFAULT_OUTPUT_PAGE_SIZE },
    playground.localOutputs.length,
  );

  const hasNext = !!initialRunGroupData?.has_next;
  const hasPrev = !!initialRunGroupData?.has_prev;
  const nextCursor = initialRunGroupData?.next_cursor ?? null;

  const total = initialRunGroupData?.total || 0;

  const initialOutputs = useMemo(
    () =>
      initialRunGroupData?.run_groups?.flatMap(group => {
        if (group.is_in_progress && playground.operationStore.getOperation(group.id)) {
          return [];
        }

        const { shared_prompt_blueprint } = group;
        const individual_run_requests = group.individual_run_requests.filter(
          run => run.status !== "cancelled",
        );

        if (individual_run_requests.length === 0) {
          return [];
        }

        const messages: IMessageStore[] = [];
        const sets = individual_run_requests.map(run => {
          let message: IMessageStore | undefined;
          if (run.message) {
            // Auto-execution returns a list; output card shows the final assistant turn.
            const singleMessage = Array.isArray(run.message)
              ? run.message[run.message.length - 1]
              : run.message;
            if (singleMessage) {
              message = new MessageStore(singleMessage);
            }
          } else if (run.content?.length) {
            const outputOnly = run.content.filter(c => c.type === "output_media");
            message = new MessageStore({
              role: "assistant",
              content: outputOnly.length ? outputOnly : run.content,
            });
          }
          if (message) {
            messages.push(message);
          }

          return {
            id: String(run.id),
            variables: [],
            request_id: run.request_id ?? undefined,
            completed: !["queued", "running"].includes(run.status),
            error: run.status === "failed" ? run.status_message || undefined : undefined,
            position: run.run_group_position,
            run: {
              id: Number(run.id),
              input_variables: run.input_variables,
              is_chat: run.is_chat,
              model_provider: shared_prompt_blueprint.metadata?.model?.provider ?? null,
              prompt_blueprint: run.prompt_blueprint ?? shared_prompt_blueprint,
              request:
                run.price !== null
                  ? {
                      price: run.price,
                    }
                  : null,
              created_at: run.created_at,
              finished_at: run.finished_at,
              request_id: run.request_id,
              status: run.status,
              status_message: run.status_message || undefined,
              user_id: run.user_id,
              workspace_id: run.workspace_id,
            } as IndividualRun,
            message_id: message?.id || undefined,
          };
        });

        const input_variable_sets = group.individual_run_requests.map(v => ({
          id: v4(),
          variables: Object.entries(v.input_variables).map(([key, value]) => ({
            id: v4(),
            key,
            value: toVariableValue(value),
          })),
        }));

        const instance = new PlaygroundStore({
          workspace_id: playground.workspace_id,
          instance_id: playground.instanceId,
          input_variable_sets,
          userToken: userToken,
          prompt_blueprint: shared_prompt_blueprint,
        });

        messages.forEach(message => message && instance.addMessage(message.toMessage()));

        instance.setCurrentOperation({
          id: group.id,
          sets: sets.reduce(
            (acc, set) => {
              acc[set.id] = set;
              return acc;
            },
            {} as Record<string, VariableSetOperation>,
          ),
        });
        return [instance];
      }) || [],
    [initialRunGroupData?.run_groups, playground, userToken],
  );

  // Reconcile stuck "Generating..." when completion websocket was missed.
  useEffect(() => {
    initialRunGroupData?.run_groups?.forEach(group => {
      if (!group.is_in_progress && playground.operationStore.getOperation(group.id)) {
        const freshOutput = initialOutputs.find(output => output.currentOperation?.id === group.id);
        playground.reconcileMissedRunCompletion(group.id, freshOutput);
      }
    });
  }, [initialRunGroupData?.run_groups, initialOutputs, playground]);

  useEffect(() => {
    if (initialOutputs && !isPreviousData) {
      playground.mergeInitialOutputsPage(initialOutputs, beforeCursor === null);
    }
  }, [beforeCursor, initialOutputs, isPreviousData, playground]);

  return {
    hasNext,
    hasPrev,
    total,
    nextCursor,
    isFetching,
    isLoading,
  };
};

export default useInitialOutputs;
